import type { AgencyResponse } from '@saaga/shared/interfaces/agency.interface'
import { useGetAgencyAnyQuery } from '@saaga/shared/store/agency-endpoints.store'
import { logger } from '@saaga/shared/utils/logger.util'
import { useCallback, useMemo, useState } from 'react'

import type { ChatInputMode } from '@/components/chat/chat-input-mode.type'
import type { PromptTemplate } from '@/components/chat/prompt-template.interface'
import { parseTemplateVariables } from '@/components/organisms/template-variable-editor/template-variable-parser.util'
import { mapAgencyToAgency } from '@/routes/agencies/agency-response-mapper.util'

import type { UseChatAttachmentsReturn } from './use-chat-attachments.hook'
import { useChatAttachments } from './use-chat-attachments.hook'
import type { UseChatTemplateFillReturn } from './use-chat-template-fill.hook'
import { useChatTemplateFill } from './use-chat-template-fill.hook'
import { useChatTemplateList } from './use-chat-template-list.hook'

const FILE_INPUT_TYPES = new Set(['image', 'pdf', 'csv'])

const checkFileAttachSupport = (response: AgencyResponse | undefined): boolean => {
  if (!response) return true
  const inputTypeList = mapAgencyToAgency(response).supportedInputTypeList ?? []
  return inputTypeList.some((t) => FILE_INPUT_TYPES.has(t))
}

export interface UseChatInputProps {
  agencySlug?: string
}

export interface UseChatInputReturn extends UseChatTemplateFillReturn, UseChatAttachmentsReturn {
  inputValue: string
  inputMode: ChatInputMode
  templateList: PromptTemplate[]
  isLoadingTemplates: boolean
  isFileAttachEnabled: boolean
  setInputValue: (value: string) => void
  setInputMode: (mode: ChatInputMode) => void
  handleAttachFromChats: () => void
  handleTemplateSelect: (templateId: string) => void
  handleCreateTemplate: () => void
  clearInput: () => void
}

export const useChatInput = ({ agencySlug }: UseChatInputProps = {}): UseChatInputReturn => {
  const [inputValue, setInputValue] = useState('')
  const [inputMode, setInputMode] = useState<ChatInputMode>('message')
  const attachments = useChatAttachments()
  const templateFill = useChatTemplateFill({ setInputValue })
  const { templateList, isLoadingTemplates } = useChatTemplateList(agencySlug)

  const { data: agencyResponse } = useGetAgencyAnyQuery(agencySlug ?? '', { skip: !agencySlug })
  const isFileAttachEnabled = useMemo(
    () => checkFileAttachSupport(agencyResponse),
    [agencyResponse]
  )
  const handleAttachFromChats = useCallback(() => logger.debug('Open chat attachment browser'), [])
  const handleCreateTemplate = useCallback(() => logger.debug('Create new template requested'), [])
  const clearInput = useCallback(() => {
    setInputValue('')
    attachments.clearAttachmentList()
  }, [attachments])

  const handleTemplateSelect = useCallback(
    (templateId: string) => {
      const template = templateList.find((t) => t.id === templateId)
      if (!template) return
      const variableList = parseTemplateVariables(template.content)
      if (variableList.length === 0) {
        setInputValue(template.content)
        return
      }
      templateFill.openTemplateFill(template)
    },
    [templateFill, templateList]
  )

  return {
    inputValue,
    inputMode,
    templateList,
    isLoadingTemplates,
    isFileAttachEnabled,
    ...attachments,
    ...templateFill,
    setInputValue,
    setInputMode,
    handleAttachFromChats,
    handleTemplateSelect,
    handleCreateTemplate,
    clearInput,
  }
}
